import { useRef, useState } from 'react'
import { ImageIcon, Trash2, Upload } from 'lucide-react'
import { cn } from '../../lib/cn.js'
import { readableSize, uploadTo } from '../../lib/upload.js'
import { Button } from './Button.js'
import { Field } from './Field.js'
import { Input } from './Input.js'
import { Text } from './Typography.js'
import { Stack } from '../layout/Stack.js'
import { StatusBanner } from '../feedback/StatusBanner.js'

export interface ImageUploadProps {
  claimUrl: string
  /**
   * What the claim route wants beside the type and the size.
   *
   * `POST /v1/admin/uploads` requires `kind: 'event_cover'` and takes the alt text;
   * the class-resource route takes neither. The caller names its route's shape
   * rather than this component guessing at one of them.
   */
  claimBody?: Record<string, unknown>
  /** The asset already on the record, if there is one. */
  value: { assetId: string; url?: string | null; alt?: string | null } | null
  onChange: (value: { assetId: string; alt: string } | null) => void
  maxBytes?: number
  label?: string
  disabled?: boolean
}

/**
 * A picture, with the words that stand in for it.
 *
 * **Alt text is part of the component, not an afterthought.** The assets table
 * carries it and the reason is written on the column: it is *"what a screen
 * reader says instead of the picture"*, and *"a client that invents one from the
 * event's title describes the event rather than the image"*. Only the person
 * uploading it can write it, so this is where it is asked for — 9.7a's image and
 * 9.6a's cover both.
 *
 * The preview is the chosen file, shown immediately from the browser rather than
 * waited for from the store: a cover image that appears three seconds after it is
 * chosen reads as an upload that failed.
 *
 * **It is one row, not a billboard.** This used to draw a 16:9 dashed rectangle
 * at the full width of the form — inside a 560px sheet that is 512 by 288, so
 * the first thing anybody saw on opening "A new event" was an empty grey box
 * taller than four fields, and the fields themselves started below the fold.
 * A cover is a thumbnail everywhere it is ever shown; the picker is now the
 * shape of the thing it picks, sitting on one line like every other control on
 * the form, and the preview is a 16:9 chip beside the words.
 *
 * Dropping works, because a desk has a folder open beside it — `FileUpload`
 * took a drop and this did not, for no reason anybody wrote down.
 *
 * **There is a way to take it off again.** `onChange` has always accepted
 * `null` and nothing ever passed it: an image put on an event by mistake was
 * on it for good.
 */
export function ImageUpload({
  claimUrl,
  claimBody,
  value,
  onChange,
  maxBytes = 12 * 1024 * 1024,
  label = 'Add an image',
  disabled,
}: ImageUploadProps) {
  const input = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [preview, setPreview] = useState<string | null>(null)
  const [alt, setAlt] = useState(value?.alt ?? '')
  const [over, setOver] = useState(false)

  const take = async (file: File | undefined) => {
    if (!file) return
    setError(null)
    if (!file.type.startsWith('image/')) return setError('That is not an image.')
    if (file.size > maxBytes) {
      return setError(
        `That image is ${readableSize(file.size)}. The most this takes is ${readableSize(maxBytes)}.`,
      )
    }
    setBusy(true)
    try {
      const objectUrl = URL.createObjectURL(file)
      setPreview(objectUrl)
      // The alt text goes up with the claim, because the store records it against
      // the asset and this is the only moment anybody is looking at the picture.
      const { assetId } = await uploadTo(claimUrl, file, {
        ...claimBody,
        ...(alt.trim() ? { alt_text: alt.trim() } : {}),
      })
      onChange({ assetId, alt })
    } catch (e) {
      setError((e as Error).message)
      setPreview(null)
    } finally {
      setBusy(false)
      if (input.current) input.current.value = ''
    }
  }

  /**
   * The picture that is already there, when the caller could find it.
   *
   * A caller holding only an asset id — which both editors did, because a
   * class row carries `cover_asset_id` and nothing else — drew a grey square
   * reading "Cover image", so the only way to check the picture was right was
   * to open the phone app and look. The **caller** fetches the link now, with
   * `useAssetUrl`: this component asks for no store of its own, because it is
   * drawn in a test that assembles screens out of the layer with nothing
   * behind them, and a component that quietly needs a `<Provider>` is one
   * that breaks somewhere it is not being looked at.
   */
  const shown = preview ?? value?.url ?? null
  const chosen = shown !== null || value !== null

  const clear = () => {
    setPreview(null)
    setAlt('')
    setError(null)
    onChange(null)
  }

  return (
    <Stack gap={3}>
      <div
        onDragOver={(e) => {
          e.preventDefault()
          setOver(true)
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => {
          e.preventDefault()
          setOver(false)
          void take(e.dataTransfer.files[0])
        }}
        className={cn(
          /* One row from `sm` up; below it the picture and words keep their
             row — a 96px thumbnail beside two short lines reads fine at any
             width — and the buttons drop to their own full-width line, for
             the same reason `FileUpload` does this: squeezed onto one row
             with a 96px image and a button, the words had under 100px and
             wrapped into a cramped stack nobody could read comfortably on a
             phone. */
          'flex flex-col gap-3 rounded-md border p-4 transition-colors sm:flex-row sm:items-center sm:gap-4 sm:p-3',
          chosen ? 'border-hairline bg-surface' : 'border-dashed',
          over ? 'border-coral-fill bg-coral-soft' : chosen ? '' : 'border-hairline-strong bg-sunk',
          disabled && 'pointer-events-none opacity-50',
        )}
      >
        <div className="flex min-w-0 items-center gap-4">
          {/* 16:9, because that is the shape it is cropped to in the app — and
              96px wide, because that is roughly how big it is there too. */}
          <div className="bg-sunk border-hairline flex aspect-[16/9] w-24 shrink-0 items-center justify-center overflow-hidden rounded-sm border">
            {shown ? (
              <img src={shown} alt={alt || ''} className="size-full object-cover" />
            ) : (
              <ImageIcon className="text-ink-muted size-5" aria-hidden />
            )}
          </div>

          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <Text as="span" variant="body">
              {chosen ? 'Cover image' : 'Drop an image here, or choose one'}
            </Text>
            <Text as="span" variant="caption">
              Shown at 16:9 · up to {readableSize(maxBytes)}
            </Text>
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            className="flex-1 sm:flex-initial"
            loading={busy}
            leadingIcon={<Upload />}
            onClick={() => input.current?.click()}
          >
            {/* The board's own words, on 9.7a. "Replace the image" reads better and
                is not what the designer drew, and on this surface the board wins. */}
            {chosen ? 'Replace' : label}
          </Button>
          {chosen && (
            <Button variant="ghost" size="icon-sm" aria-label="Remove the image" onClick={clear}>
              <Trash2 />
            </Button>
          )}
        </div>
      </div>
      <input
        ref={input}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => void take(e.target.files?.[0])}
      />

      {chosen && (
        <Field
          label="What a screen reader says instead of the picture"
          hint="Describe the image, not the thing it is for. Somebody who cannot see it should know what is in it."
        >
          {(f) => (
            <Input
              {...f}
              value={alt}
              placeholder="A person sitting cross-legged on a jetty at sunrise"
              onChange={(e) => {
                setAlt(e.target.value)
                if (value) onChange({ assetId: value.assetId, alt: e.target.value })
              }}
            />
          )}
        </Field>
      )}

      {error && <StatusBanner tone="error">{error}</StatusBanner>}
    </Stack>
  )
}
